/**
 * AI:
 * Status: "in progress"
 * Type: Service
 * SubType: WidgetConfig
 * Reason: Widget type registry of the QL-Widget-Builder - per type (table, list, chart, category) the required
 *   mapping keys and the WidgetFactory call with dataSource ql / dataProvider list / dataProviderObject
 *   = start entity
 */
import {WidgetFactory} from "../../shared/widget/service/widget.factory";
import {WidgetData} from "../../shared/widget/widget-data";
import {WidgetDataParam} from "../../shared/widget/widget-data-param";
import {UiQlWidgetBuildIssue} from "../interface/ui-ql-widget-build-issue.interface";
import {
    UiQlWidgetCategoryMappingDto,
    UiQlWidgetChartMappingDto,
    UiQlWidgetMapping,
    UiQlWidgetType,
} from "../model/dto/api/ui-ql-widget-mapping.dto";
import {UiQlWidgetTypeRegistryEntry} from "../interface/ui-ql-widget-type-registry-entry.interface";
import {UiQlWidgetCreateContext} from "../interface/ui-ql-widget-create-context.interface";
import {UiQlWidgetColumns} from "../service/domain/ui-ql-widget-columns";

/** `chartType` values of the schema-2 chart mapping (api-contract.md). */
const CHART_TYPES: ReadonlyArray<string> = ['bar', 'line', 'pie', 'area'];

/** `chartAggregateFunction` values of the schema-2 chart mapping (api-contract.md). */
const CHART_AGGREGATE_FUNCTIONS: ReadonlyArray<string> = ['count', 'sum', 'avg', 'min', 'max'];

/** `displayMode` values the category widget knows (`MvsWidgetCategoryComponent.displayModes`). */
const CATEGORY_DISPLAY_MODES: ReadonlyArray<string> = [
    'simple', 'progress', 'doughnut', 'bar', 'line', 'polarArea', 'radar',
];

/**
 * The chart widget aggregates the rows it receives once more on the client. A QL-Widget-Builder
 * chart is ALWAYS fed a server-side groupBy — one row per group, the aggregate already computed
 * by the engine — so the client step must be the identity. `sum` over one row is exactly that
 * value; handing the mapping's own function through would turn `count` into "1 per group".
 * The mapping's `chartAggregateFunction` documents the SERVER aggregate of `chartFieldAggregate`.
 */
const CHART_CLIENT_AGGREGATE_ON_SERVER_GROUPS = 'sum';

function violation(path: string, message: string): UiQlWidgetBuildIssue {
    return {code: 'mappingInvalid', path, message};
}

function checkResultKey(
    violations: UiQlWidgetBuildIssue[],
    resultKeys: string[] | null,
    key: string,
    value: string | undefined): void {

    if (!value || !resultKeys) {
        return;
    }
    if (!resultKeys.includes(value)) {
        violations.push(violation(
            `mapping.${key}`,
            `Das Feld „${value}" kommt im Ergebnis der Abfrage nicht vor.`));
    }
}

/** Parameters every type shares: the ones the builder already derived, unchanged. */
function withTypeParams(context: UiQlWidgetCreateContext, ...typeParams: WidgetDataParam[]): WidgetDataParam[] {
    return [...context.params, ...typeParams];
}

/**
 * Table/list column layout: the definition's columns in definition order with their German
 * headers, as the `widgetForm` parameter the widget's layout builder honours. Without columns
 * (nothing to derive) the widget keeps the engine's form.
 */
function columnLayoutParams(context: UiQlWidgetCreateContext): WidgetDataParam[] {
    const widgetForm = UiQlWidgetColumns.widgetForm(context.columns ?? []);
    return widgetForm ? [WidgetDataParam.create('widgetForm', widgetForm)] : [];
}

const TABLE_ENTRY: UiQlWidgetTypeRegistryEntry = {
    widgetType: 'table',
    requiredMappingKeys: [],
    requiresGroupBy: false,
    allowsSideCar: true,
    validateMapping: () => [],

    create(context: UiQlWidgetCreateContext): WidgetData {
        /**
         * AI:
         * Status: "in progress"
         * Type: Widget
         * SubType: Runtime
         * Reason: QL-Widget-Builder table - renders the columns of a user-built, policy-checked
         *   QlQueryDto in definition order (widgetForm layout from UiQlWidgetColumns); the request
         *   (view or simple pipe, with or without count) is prepared by UiQlWidgetDataBuilder, so
         *   the generic list factory is used instead of createWidgetTableQl, which can only emit
         *   the view pipe with count.
         */
        return WidgetFactory.createWidgetList(
            context.alias,
            context.name,
            'table',
            'list',
            'ql',
            context.entity,
            context.noDataText,
            context.request,
            ...withTypeParams(context, ...columnLayoutParams(context)));
    },
};

const LIST_ENTRY: UiQlWidgetTypeRegistryEntry = {
    widgetType: 'list',
    requiredMappingKeys: [],
    requiresGroupBy: false,
    allowsSideCar: true,
    validateMapping: () => [],

    create(context: UiQlWidgetCreateContext): WidgetData {
        /**
         * AI:
         * Status: "in progress"
         * Type: Widget
         * SubType: Runtime
         * Reason: QL-Widget-Builder list - card list over the rows of a user-built, policy-checked
         *   QlQueryDto, fields in definition order (widgetForm layout from UiQlWidgetColumns); same
         *   prepared request as the table (pipe and count chosen by UiQlWidgetDataBuilder), hence
         *   the generic list factory instead of createWidgetListQl.
         */
        return WidgetFactory.createWidgetList(
            context.alias,
            context.name,
            'list',
            'list',
            'ql',
            context.entity,
            context.noDataText,
            context.request,
            ...withTypeParams(context, ...columnLayoutParams(context)));
    },
};

const CHART_ENTRY: UiQlWidgetTypeRegistryEntry = {
    widgetType: 'chart',
    requiredMappingKeys: ['chartType', 'chartField1st', 'chartFieldAggregate', 'chartAggregateFunction'],
    requiresGroupBy: true,
    allowsSideCar: false,

    validateMapping(mapping: UiQlWidgetMapping, resultKeys: string[] | null): UiQlWidgetBuildIssue[] {
        const chart = mapping as UiQlWidgetChartMappingDto;
        const violations: UiQlWidgetBuildIssue[] = [];

        if (!CHART_TYPES.includes(chart.chartType)) {
            violations.push(violation('mapping.chartType', `Unbekannte Diagrammart „${chart.chartType}".`));
        }
        if (!CHART_AGGREGATE_FUNCTIONS.includes(chart.chartAggregateFunction)) {
            violations.push(violation(
                'mapping.chartAggregateFunction',
                `Unbekannte Aggregatfunktion „${chart.chartAggregateFunction}".`));
        }
        checkResultKey(violations, resultKeys, 'chartField1st', chart.chartField1st);
        checkResultKey(violations, resultKeys, 'chartFieldAggregate', chart.chartFieldAggregate);
        checkResultKey(violations, resultKeys, 'chartField2nd', chart.chartField2nd);

        return violations;
    },

    create(context: UiQlWidgetCreateContext): WidgetData {
        const chart = context.mapping as UiQlWidgetChartMappingDto;

        const typeParams = [
            WidgetDataParam.create('chartType', chart.chartType),
            WidgetDataParam.create('chartField1st', chart.chartField1st),
            WidgetDataParam.create('chartFieldAggregate', chart.chartFieldAggregate),
            WidgetDataParam.create('chartAggregateFunction', CHART_CLIENT_AGGREGATE_ON_SERVER_GROUPS),
        ];
        if (chart.chartField2nd) {
            typeParams.push(WidgetDataParam.create('chartField2nd', chart.chartField2nd));
        }

        // legend = the metric's German label ("Anzahl"); axis = the date-group precision so the
        // chart formats a month bucket as a month instead of the raw UTC instant
        const valueColumn = UiQlWidgetColumns.find(context.columns, chart.chartFieldAggregate);
        if (valueColumn?.label) {
            typeParams.push(WidgetDataParam.create('chartValueLabel', valueColumn.label));
        }
        const axisColumn = UiQlWidgetColumns.find(context.columns, chart.chartField1st);
        if (axisColumn?.dateGroupPrecision) {
            typeParams.push(WidgetDataParam.create('chartField1stDateGroup', axisColumn.dateGroupPrecision));
        }

        /**
         * AI:
         * Status: "in progress"
         * Type: Widget
         * SubType: Runtime
         * Reason: QL-Widget-Builder chart - bar/line/pie/area diagram over a server-side groupBy
         *   QlQueryDto (one row per group); axis and value fields come from the schema-2 chart
         *   mapping, legend label and axis date-group precision from the derived columns, the
         *   request is prepared without count by UiQlWidgetDataBuilder.
         */
        return WidgetFactory.createWidgetList(
            context.alias,
            context.name,
            'chart',
            'list',
            'ql',
            context.entity,
            context.noDataText,
            context.request,
            ...withTypeParams(context, ...typeParams));
    },
};

const CATEGORY_ENTRY: UiQlWidgetTypeRegistryEntry = {
    widgetType: 'category',
    requiredMappingKeys: ['fieldCategory', 'fieldCategoryCount'],
    requiresGroupBy: true,
    allowsSideCar: false,

    validateMapping(mapping: UiQlWidgetMapping, resultKeys: string[] | null): UiQlWidgetBuildIssue[] {
        const category = mapping as UiQlWidgetCategoryMappingDto;
        const violations: UiQlWidgetBuildIssue[] = [];

        if (category.displayMode && !CATEGORY_DISPLAY_MODES.includes(category.displayMode)) {
            violations.push(violation('mapping.displayMode', `Unbekannte Darstellung „${category.displayMode}".`));
        }
        checkResultKey(violations, resultKeys, 'fieldCategory', category.fieldCategory);
        checkResultKey(violations, resultKeys, 'fieldCategoryCount', category.fieldCategoryCount);

        return violations;
    },

    create(context: UiQlWidgetCreateContext): WidgetData {
        const category = context.mapping as UiQlWidgetCategoryMappingDto;

        const typeParams = [
            WidgetDataParam.create('fieldCategory', category.fieldCategory),
            WidgetDataParam.create('fieldCategoryCount', category.fieldCategoryCount),
        ];
        if (category.displayMode) {
            typeParams.push(WidgetDataParam.create('displayMode', category.displayMode));
        }

        /**
         * AI:
         * Status: "in progress"
         * Type: Widget
         * SubType: Runtime
         * Reason: QL-Widget-Builder category - counts per group of a server-side groupBy
         *   QlQueryDto (e.g. tickets per status); category and count fields come from the
         *   schema-2 category mapping, the request is prepared without count.
         */
        return WidgetFactory.createWidgetList(
            context.alias,
            context.name,
            'category',
            'list',
            'ql',
            context.entity,
            context.noDataText,
            context.request,
            ...withTypeParams(context, ...typeParams));
    },
};

/**
 * Widget type → required mapping keys + `WidgetFactory` call (requirement.md "Datenschicht:
 * Registry Typ → Mapping + Factory-Aufruf"). A new widget type is ONE entry here.
 */
export const UI_QL_WIDGET_TYPE_REGISTRY: Readonly<Record<UiQlWidgetType, UiQlWidgetTypeRegistryEntry>> = {
    table: TABLE_ENTRY,
    list: LIST_ENTRY,
    chart: CHART_ENTRY,
    category: CATEGORY_ENTRY,
};
